import React, { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import usePriceSubscription from '../../hooks/usePriceSubscription';
import MarketDataService from '../../services/market/MarketDataService';

const ChartHUDOverlay = ({ symbol, item, theme }) => {
  const price = usePriceSubscription(symbol);
  const icons = useSelector(state => state.platform.icons);
  const icon = icons[symbol];
  const meta = item || MarketDataService.getSymbolMeta(symbol);

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const digits = meta?.digits ?? price?.digits ?? 5;
  const bid = price?.bid;
  const ask = price?.ask;
  const spread = MarketDataService.formatSpreadInPips(price?.spread, digits, price);
  const marketName = meta?.description || meta?.market || meta?.category || 'Financial Asset';
  const marketBadge = meta?.market || meta?.category || 'Market';

  const formatNum = useCallback((val, dec = digits) => {
    if (val === undefined || val === null) return '-';
    const num = Number(val);
    return Number.isFinite(num) ? num.toFixed(dec) : '-';
  }, [digits]);

  if (!symbol) return null;

  const Metric = ({ label, value, color = theme.text, background = theme.card2, wide = false }) => (
    <div style={{
      minWidth: wide ? 106 : 82,
      height: 38,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: 3,
      padding: '0 10px',
      borderRadius: 7,
      background,
      border: `1px solid ${theme.border}88`,
      flexShrink: 0,
    }}>
      <span style={{ fontSize: 8, fontWeight: 800, color: theme.textM, lineHeight: 1, textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ fontFamily: theme.mono, fontSize: 11, fontWeight: 800, color, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </div>
  );

  return (
    <div style={{
      width: '100%',
      minHeight: isSmallScreen ? 50 : 60,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: isSmallScreen ? 8 : 10,
      background: theme.surface,
      borderBottom: `1px solid ${theme.border}`,
      padding: isSmallScreen ? '7px 10px' : '9px 14px',
      overflowX: 'auto',
      overflowY: 'hidden',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minWidth: isSmallScreen ? 150 : 250,
        maxWidth: isSmallScreen ? 190 : 330,
        height: isSmallScreen ? 36 : 42,
        padding: isSmallScreen ? '0 8px' : '0 10px',
        borderRadius: 8,
        background: theme.card,
        border: `1px solid ${theme.border}`,
        flexShrink: 0,
      }}>
        {icon ? (
          <img
            src={icon}
            alt={symbol}
            style={{
              width: isSmallScreen ? 24 : 28,
              height: isSmallScreen ? 24 : 28,
              borderRadius: '50%',
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
        ) : (
          <div style={{
            width: isSmallScreen ? 24 : 28,
            height: isSmallScreen ? 24 : 28,
            borderRadius: '50%',
            background: theme.primaryGlow,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 8,
            fontWeight: 900,
            color: theme.primaryL,
            flexShrink: 0,
          }}>
            {symbol.slice(0, 3)}
          </div>
        )}

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontSize: isSmallScreen ? 12 : 14, fontWeight: 900, color: theme.text, lineHeight: 1 }}>
              {symbol}
            </span>
            {!isSmallScreen && (
              <span style={{
                fontSize: 8,
                fontWeight: 800,
                color: theme.primaryL,
                background: theme.primaryGlow,
                borderRadius: 5,
                padding: '2px 5px',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}>
                {marketBadge}
              </span>
            )}
          </div>

          {!isSmallScreen && (
            <div style={{
              fontSize: 9,
              color: theme.textS,
              fontWeight: 600,
              marginTop: 4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {marketName}
            </div>
          )}
        </div>
      </div>

      <Metric label="Bid" value={formatNum(bid)} color={theme.negL || theme.neg} background={theme.negBg} wide />
      <Metric label="Ask" value={formatNum(ask)} color={theme.posL || theme.pos} background={theme.posBg} wide />
      <Metric label="Spread" value={spread} color={theme.orange} background={theme.orangeBg} />

      {!isSmallScreen && (
        <div style={{ width: 1, height: 32, background: theme.border, flexShrink: 0 }} />
      )}

      {!isSmallScreen && (
        <>
          <Metric label="High" value={formatNum(price?.high)} color={theme.pos} />
          <Metric label="Low" value={formatNum(price?.low)} color={theme.neg} />
        </>
      )}
    </div>
  );
};

export default memo(ChartHUDOverlay);
